import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Search, Trash2, Pencil, X, UserPlus, Lock, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { apiFetch } from '../lib/api';

const Team = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [newSkill, setNewSkill] = useState('');
  const [accountLimits, setAccountLimits] = useState(null);

  const [formData, setFormData] = useState({
    name: '', initials: '', role: '', email: '', phone: '', skills: [], availability: 'Disponible'
  });
  const [showCustomRole, setShowCustomRole] = useState(false);
  const [newCustomRole, setNewCustomRole] = useState('');
  const [customRoles, setCustomRoles] = useState([]);

  const defaultRoles = [
    'Assistant(e) administratif(ve)', 'Gestionnaire de projet', 'Entrepreneur',
    'Freelance', 'Consultant', 'Coach / Formateur', 'Responsable administratif',
    'Office manager', 'Commercial', 'Responsable communication'
  ];
  const allRoles = [...defaultRoles, ...customRoles.filter(r => !defaultRoles.includes(r))];

  const fetchMembers = useCallback(async () => {
    try {
      const res = await apiFetch('/api/team');
      if (res.ok) setMembers(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMembers(); fetchAccountLimits(); }, [fetchMembers]);

  const fetchAccountLimits = useCallback(async () => {
    try {
      const res = await apiFetch('/api/account/limits');
      if (res.ok) setAccountLimits(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const fetchCustomRoles = useCallback(async () => {
    try {
      const res = await apiFetch('/api/team-roles');
      if (res.ok) setCustomRoles(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchMembers(); fetchAccountLimits(); fetchCustomRoles(); }, [fetchMembers]);

  useEffect(() => {
    if (!searchQuery) { setFilteredMembers(members); return; }
    const q = searchQuery.toLowerCase();
    setFilteredMembers(members.filter(m =>
      m.name?.toLowerCase().includes(q) || m.role?.toLowerCase().includes(q) ||
      m.skills?.some(s => s.toLowerCase().includes(q))
    ));
  }, [members, searchQuery]);

  const resetForm = () => {
    setFormData({ name: '', initials: '', role: '', email: '', phone: '', skills: [], availability: 'Disponible' });
    setNewSkill('');
    setShowCustomRole(false);
    setNewCustomRole('');
  };

  const handleAddCustomRole = async () => {
    const name = newCustomRole.trim();
    if (!name) return;
    try {
      await apiFetch('/api/team-roles', { method: 'POST', body: JSON.stringify({ name }) });
      await fetchCustomRoles();
      setFormData({ ...formData, role: name });
      setShowCustomRole(false);
      setNewCustomRole('');
    } catch (e) { console.error(e); }
  };

  const handleNameChange = (name) => {
    const parts = name.trim().split(' ');
    const initials = parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
    setFormData({ ...formData, name, initials });
  };

  const addSkill = () => {
    const skill = newSkill.trim();
    if (skill && !formData.skills.includes(skill)) {
      setFormData({ ...formData, skills: [...formData.skills, skill] });
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setFormData({ ...formData, skills: formData.skills.filter(s => s !== skill) });
  };

  const handleAddMember = async () => {
    try {
      const res = await apiFetch('/api/team', { method: 'POST', body: JSON.stringify(formData) });
      if (res.ok) { await fetchMembers(); await fetchAccountLimits(); setIsAddDialogOpen(false); resetForm(); }
    } catch (e) { console.error(e); }
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name || '', initials: member.initials || '', role: member.role || '',
      email: member.email || '', phone: member.phone || '', skills: member.skills || [],
      availability: member.availability || 'Disponible'
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const res = await apiFetch(`/api/team/${editingMember.id}`, { method: 'PUT', body: JSON.stringify(formData) });
      if (res.ok) { await fetchMembers(); setIsEditDialogOpen(false); setEditingMember(null); resetForm(); }
    } catch (e) { console.error(e); }
  };

  const handleDeleteMember = async (id) => {
    if (!window.confirm('Supprimer ce membre de l\'équipe ?')) return;
    try {
      const res = await apiFetch(`/api/team/${id}`, { method: 'DELETE' });
      if (res.ok) await fetchMembers();
    } catch (e) { console.error(e); }
  };

  const getWorkloadColor = (w) => {
    if (w >= 80) return 'text-red-600';
    if (w >= 50) return 'text-amber-600';
    return 'text-emerald-600';
  };

  const memberFormJSX = (
    <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Nom complet *</label>
          <Input value={formData.name} onChange={(e) => handleNameChange(e.target.value)} placeholder="Ex: Sophie Martin" data-testid="member-name-input" />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Initiales</label>
          <Input value={formData.initials} onChange={(e) => setFormData({ ...formData, initials: e.target.value })} placeholder="SM" maxLength={3} data-testid="member-initials-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Rôle *</label>
          {showCustomRole ? (
            <div className="flex gap-2">
              <Input value={newCustomRole} onChange={(e) => setNewCustomRole(e.target.value)} placeholder="Nouveau rôle..." className="flex-1" autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleAddCustomRole()} data-testid="member-custom-role-input" />
              <Button size="sm" onClick={handleAddCustomRole} disabled={!newCustomRole.trim()}>OK</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowCustomRole(false)}><X className="w-4 h-4" /></Button>
            </div>
          ) : (
            <Select value={formData.role} onValueChange={(v) => v === '__custom__' ? setShowCustomRole(true) : setFormData({ ...formData, role: v })}>
              <SelectTrigger data-testid="member-role-select"><SelectValue placeholder="Sélectionner un rôle" /></SelectTrigger>
              <SelectContent>
                {allRoles.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                <SelectItem value="__custom__">+ Créer un rôle</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Disponibilité</label>
          <Select value={formData.availability} onValueChange={(v) => setFormData({ ...formData, availability: v })}>
            <SelectTrigger data-testid="member-availability-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Disponible">Disponible</SelectItem>
              <SelectItem value="Partiellement disponible">Partiellement disponible</SelectItem>
              <SelectItem value="Indisponible">Indisponible</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Email *</label>
          <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="sophie@agence.fr" data-testid="member-email-input" />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Téléphone</label>
          <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="06 12 34 56 78" data-testid="member-phone-input" />
        </div>
      </div>
      {/* Skills */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Compétences</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            placeholder="Ajouter une compétence..."
            className="flex-1 h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSkill(); } }}
            data-testid="member-skill-input"
          />
          <Button type="button" size="sm" onClick={addSkill} disabled={!newSkill.trim()} data-testid="member-add-skill-btn">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        {formData.skills.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.skills.map(skill => (
              <Badge key={skill} variant="secondary" className="flex items-center gap-1">
                {skill}
                <X className="w-3 h-3 cursor-pointer hover:text-red-500" onClick={() => removeSkill(skill)} />
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const isFree = accountLimits?.account_type === 'free';
  const canAddMember = !accountLimits || accountLimits.can_create_team_member;

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Free limit banner */}
      {isFree && !canAddMember && (
        <Card className="p-4 border-amber-200 bg-amber-50" data-testid="free-team-banner">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">Limite du plan gratuit atteinte</p>
                <p className="text-sm text-amber-700">Votre compte gratuit vous permet d'ajouter 1 membre d'équipe. Passez au plan Pro pour gérer une équipe complète.</p>
              </div>
            </div>
            <Button size="sm" onClick={() => navigate('/pricing')} className="flex-shrink-0" data-testid="upgrade-btn-team">
              Passer au Pro <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">Équipe Projet</h1>
          <p className="text-base text-muted-foreground mt-2">{members.length} membres</p>
        </div>
        {canAddMember ? (
          <Button onClick={() => { resetForm(); setIsAddDialogOpen(true); }} data-testid="add-member-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Ajouter un membre
          </Button>
        ) : (
          <Button variant="outline" onClick={() => navigate('/pricing')} className="text-muted-foreground" data-testid="add-member-btn-disabled">
            <Lock className="w-4 h-4 mr-2" /> Ajouter un membre
          </Button>
        )}
      </div>

      <Card className="p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Rechercher par nom, rôle ou compétence..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Chargement...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-grid">
          {filteredMembers.map((member, idx) => (
            <motion.div key={member.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
              <Card className="p-5 hover:shadow-lg transition-shadow h-full" data-testid={`member-card-${idx}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-bold">
                      {member.initials}
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{member.name}</h3>
                      <p className="text-sm text-muted-foreground">{member.role}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:text-blue-600 hover:bg-blue-50"
                      onClick={() => handleEditMember(member)} data-testid={`edit-member-${idx}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteMember(member.id)} data-testid={`delete-member-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Workload */}
                <div className="mb-3">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Charge de travail</span>
                    <span className={`font-semibold ${getWorkloadColor(member.current_workload || 0)}`}>
                      {member.current_workload || 0}%
                    </span>
                  </div>
                  <Progress value={member.current_workload || 0} className="h-2" />
                </div>

                {/* Skills */}
                {member.skills?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {member.skills.map(skill => (
                      <Badge key={skill} variant="secondary" className="text-xs">{skill}</Badge>
                    ))}
                  </div>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t text-xs text-muted-foreground">
                  <span>{member.email}</span>
                  <Badge variant="outline" className={`text-xs ${member.availability === 'Disponible' ? 'text-emerald-600 border-emerald-300' : member.availability === 'Indisponible' ? 'text-red-600 border-red-300' : 'text-amber-600 border-amber-300'}`}>
                    {member.availability}
                  </Badge>
                </div>
              </Card>
            </motion.div>
          ))}
          {filteredMembers.length === 0 && (
            <div className="col-span-full text-center py-12 text-muted-foreground">Aucun membre trouvé</div>
          )}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Ajouter un membre</DialogTitle>
            <DialogDescription>Ajouter un nouveau membre à l'équipe</DialogDescription>
          </DialogHeader>
          {memberFormJSX}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleAddMember} disabled={!formData.name || !formData.role || !formData.email}>
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Modifier le membre</DialogTitle>
            <DialogDescription>Mettre à jour les informations et compétences</DialogDescription>
          </DialogHeader>
          {memberFormJSX}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveEdit} disabled={!formData.name || !formData.role || !formData.email}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Team;
